import { useRef, useState, type ReactNode } from "react";
import { ApiError } from "@/core/api/apiTypes";
import { userAction } from "@/core/telemetry/userAction";
import { FeatureRequired } from "@/features/subscription/components/FeatureRequired";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FieldGroup } from "@/shared/components/ui/FieldGroup";
import { FormField } from "@/shared/components/ui/FormField";
import { FormPage } from "@/shared/components/ui/FormPage";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { Section } from "@/shared/components/ui/Section";
import { useUpdateCallForwarding } from "../hooks/phoneNumberQueries";
import {
  isE164PhoneNumber,
  normalizeForwardingNumber,
  type PhoneNumber,
} from "../types/phoneNumberTypes";
import { PhoneNumberSection } from "./PhoneNumberSection";

const forwardingMessage = "Forwarding calls to your phone is included in the Pro and Pro+AI plans.";

interface CallForwardingSectionProps {
  header: ReactNode;
  businessId: string;
  phone: PhoneNumber;
  /** The current plan doesn't include the `sms` feature that call forwarding needs. */
  locked: boolean;
}

export function CallForwardingSection({ header, businessId, phone, locked }: CallForwardingSectionProps) {
  const [value, setValue] = useState(phone.forwardToNumber ?? "");
  const [fieldError, setFieldError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [notice, setNotice] = useState("");
  const [planRequired, setPlanRequired] = useState(false);
  const [turnOffOpen, setTurnOffOpen] = useState(false);
  const [turnOffError, setTurnOffError] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const submitting = useRef(false);
  const update = useUpdateCallForwarding(businessId, phone.id);
  const busy = update.isPending;
  const active = phone.status === "active";
  const featureLocked = locked || planRequired;
  const editable = active && !featureLocked;

  function clearFeedback() {
    setFieldError("");
    setSaveError("");
    setNotice("");
  }

  async function submit(forwardToNumber: string | null) {
    submitting.current = true;
    try {
      const saved = await update.mutateAsync({ forwardToNumber });
      setValue(saved.forwardToNumber ?? "");
      setNotice(saved.forwardToNumber ? "Call forwarding saved." : "Call forwarding turned off.");
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.Status === 402) setPlanRequired(true);
      else throw error;
      return false;
    } finally {
      submitting.current = false;
    }
  }

  async function save() {
    if (submitting.current || !editable) return;
    clearFeedback();
    const forwardToNumber = normalizeForwardingNumber(value);
    if (!forwardToNumber) {
      setFieldError(phone.forwardToNumber
        ? "Enter a number, or turn off call forwarding below."
        : "Enter the number to forward calls to.");
      input.current?.focus();
      return;
    }
    if (!isE164PhoneNumber(forwardToNumber)) {
      setFieldError("Enter the number in international format, starting with + and the country code, e.g. +15551234567.");
      input.current?.focus();
      return;
    }
    if (forwardToNumber === phone.number) {
      setFieldError("Calls can't be forwarded to your Froppio number. Enter your own phone number.");
      input.current?.focus();
      return;
    }
    try {
      await submit(forwardToNumber);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Couldn't save call forwarding. Try again.");
    }
  }

  async function turnOff() {
    if (submitting.current) return;
    clearFeedback();
    setTurnOffError("");
    try {
      await submit(null);
      setTurnOffOpen(false);
    } catch (error) {
      setTurnOffError(error instanceof Error ? error.message : "Couldn't turn off call forwarding. Try again.");
    }
  }

  const content = (
    <>
      <PhoneNumberSection phone={phone} />
      <Section title="Call forwarding">
        {featureLocked ? (
          <FeatureRequired feature="sms" message={forwardingMessage} />
        ) : !active ? (
          <p className="text-sm text-muted-foreground">
            Call forwarding needs an active number. Calls to this number are declined.
          </p>
        ) : (
          <FieldGroup disabled={busy}>
            <p className="text-sm text-muted-foreground">
              Calls to your Froppio number ring this phone. Callers see their own number.
              Leave forwarding off and calls are declined.
            </p>
            <FormField
              id="forward-to-number"
              label="Forward calls to"
              description="Use international format, e.g. +15551234567."
              error={fieldError}
            >
              <Input
                ref={input}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+15551234567"
                data-telemetry-mask
                value={value}
                onChange={(event) => { clearFeedback(); setValue(event.target.value); }}
              />
            </FormField>
          </FieldGroup>
        )}
        {phone.forwardToNumber && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {editable ? "Forwarding is on." : "A forwarding number is saved, but calls are declined until this is resolved."}
            </p>
            <Button variant="outline" disabled={busy} onClick={() => { setTurnOffError(""); setTurnOffOpen(true); }}>
              Turn off call forwarding
            </Button>
          </div>
        )}
      </Section>
      {saveError && <ErrorState title="Couldn't save call forwarding" message={saveError} />}
      <p role="status" aria-live="polite" className="text-sm text-primary">{notice}</p>
    </>
  );

  return (
    <>
      {editable ? (
        <FormPage
          header={header}
          contentClassName="space-y-6"
          onSubmit={(event) => { event.preventDefault(); void save(); }}
          actions={
            <Button type="submit" {...userAction("call_forwarding.form_submit")} formNoValidate disabled={busy}>
              {busy ? "Saving..." : "Save call forwarding"}
            </Button>
          }
        >
          {content}
        </FormPage>
      ) : (
        <Page>
          {header}
          <PageContent><div className="space-y-6">{content}</div></PageContent>
        </Page>
      )}
      <ConfirmationDialog
        open={turnOffOpen}
        onOpenChange={setTurnOffOpen}
        title="Turn off call forwarding?"
        description="Calls to your Froppio number will be declined until you set a forwarding number again."
        confirmLabel="Turn off forwarding"
        dismissLabel="Keep forwarding"
        destructive
        pending={busy}
        error={turnOffError}
        onConfirm={() => void turnOff()}
      />
    </>
  );
}
